import { useCallback } from 'react'
import { atom, useAtomValue, useSetAtom } from 'jotai'

// The latest memory fact surfaced over the websocket. Module-scoped like the
// other chat atoms; the message list hosts the listener that writes it (it's
// the one component mounted for the lifetime of a visible conversation) and
// the message actions row reads it.
//
// Reads and writes are split (mirroring turn-error.ts): the write-only hooks
// use `useSetAtom` so the listener that only ever calls them never
// re-renders on the value it just wrote.

type FactEvent = {
  text: string
  timestamp: number
  processed: boolean
}

const latestFactAtom = atom<FactEvent | null>(null)

/** Latest memory fact surfaced over the websocket, or null before one arrives. */
export const useLatestFact = (): FactEvent | null => useAtomValue(latestFactAtom)

export const useUpdateLatestFact = () => {
  const setLatestFact = useSetAtom(latestFactAtom)
  return useCallback(
    (text: string) => {
      setLatestFact({
        text,
        timestamp: Date.now(),
        processed: false,
      })
    },
    [setLatestFact],
  )
}

export const useMarkFactAsProcessed = () => {
  const setLatestFact = useSetAtom(latestFactAtom)
  return useCallback(() => {
    setLatestFact((prev) => (prev ? { ...prev, processed: true } : prev))
  }, [setLatestFact])
}
